import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Trash2, AlertTriangle, ArrowLeft } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import SearchableSelect, {
  type SearchableSelectOption,
} from '../../components/SearchableSelect';
import AsyncItemSelect from '../../components/AsyncItemSelect';
import ItemFormModal from '../../components/ItemFormModal';
import CustomerFormModal from '../../components/CustomerFormModal';
import { createQuotation, updateQuotation, getQuotationById } from '../../utils/sales/quotationApi';
import { getCustomerReceivableInvoices } from '../../utils/invoiceApi';
import { getCustomerPayments } from '../../utils/customerPaymentApi';
import { getCustomers, getCustomerById } from '../../utils/customerApi';
import { ApiValidationError } from '../../types/auth';
import DecimalInput from '../../components/DecimalInput';
import { btnPrimary } from '../../utils/uiTheme';
import { blurActiveInputBeforeSave } from '../../utils/numberInput';
import { displayRate } from '../../utils/linePricing';
import { formatCurrency, formatPreviewQuantity } from '../../utils/formatCurrency';
import { toast, toastApiError } from '../../utils/toast';
import { canManageQuotations } from '../../utils/salesTransactionPermissions';
import {
  customerToSnapshot,
  emptyCustomerSnapshot,
  snapshotToPayload,
  validateCustomerSnapshot,
} from '../../utils/customerSnapshot';
import type { CustomerSnapshot } from '../../types/invoice';
import {
  getQuotationCustomerName,
  type CreateQuotationData,
  type Quotation,
  type QuotationLine,
  type QuotationStatus,
  type UpdateQuotationData,
} from '../../types/sales/quotation';
import type { Customer } from '../../types/customer';
import type { Item } from '../../types/item';
import { qtyOnHandClassName } from '../../utils/negativeInventory';
import { todayLocalIso } from '../../utils/localDate';
import { scrollIntoViewAfterPaint } from '../../utils/scrollIntoViewAfterPaint';

const QUOTATION_STATUSES: QuotationStatus[] = [
  'DRAFT',
  'SENT',
  'ACCEPTED',
  'REJECTED',
  'EXPIRED',
];

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const customerToOption = (customer: Customer): SearchableSelectOption => ({
  value: customer.id,
  label: customer.display_name,
  description:
    [customer.email, customer.phone].filter(Boolean).join(' · ') || undefined,
});

const round2 = (n: number) => Math.round(n * 100) / 100;

interface LocalLine {
  item_id: number;
  item_name: string;
  item_sku: string;
  item_type: Item['item_type'];
  qty_on_hand: string;
  dealer_selling_price: string;
  cash_price: string;
  description: string;
  quantity: number;
  unit_price: number;
  discount_amount: number;
  tax_amount: number;
}

const createEmptyLine = (): LocalLine => ({
  item_id: 0,
  item_name: '',
  item_sku: '',
  item_type: 'INVENTORY',
  qty_on_hand: '0',
  dealer_selling_price: '0',
  cash_price: '0',
  description: '',
  quantity: 1,
  unit_price: 0,
  discount_amount: 0,
  tax_amount: 0,
});

const INITIAL_LINE_COUNT = 4;

const createInitialLines = (count = INITIAL_LINE_COUNT) =>
  Array.from({ length: count }, () => createEmptyLine());

const withTrailingEmptyLine = (lines: LocalLine[]): LocalLine[] => {
  if (lines.length === 0) return [createEmptyLine()];
  if (lines[lines.length - 1].item_id > 0) return [...lines, createEmptyLine()];
  return lines;
};

const mapQuotationLineToLocal = (line: QuotationLine): LocalLine => ({
  item_id: line.item_id,
  item_name: line.item_snapshot.name,
  item_sku: line.item_snapshot.sku,
  item_type: line.item?.item_type ?? 'INVENTORY',
  qty_on_hand: line.item?.qty_on_hand ?? '0',
  dealer_selling_price: line.item?.dealer_selling_price ?? '0',
  cash_price: line.item?.cash_price ?? '0',
  description: line.description ?? '',
  quantity: parseFloat(line.quantity),
  unit_price: parseFloat(line.unit_price),
  discount_amount: parseFloat(line.discount_amount),
  tax_amount: parseFloat(line.tax_amount),
});

const computeLineTotals = (line: LocalLine) => {
  const gross = round2(line.quantity * line.unit_price);
  const discount = Math.min(line.discount_amount, gross);
  const net = round2(gross - discount);
  const lineTotal = round2(net + line.tax_amount);
  return { gross, discount, net, lineTotal };
};

const computeQuotationTotals = (lines: LocalLine[]) => {
  let subTotal = 0;
  let discountTotal = 0;
  let taxTotal = 0;

  for (const line of lines) {
    if (line.item_id === 0) continue;
    const { net, discount } = computeLineTotals(line);
    subTotal += net;
    discountTotal += discount;
    taxTotal += line.tax_amount;
  }

  return {
    subTotal: round2(subTotal),
    discountTotal: round2(discountTotal),
    taxTotal: round2(taxTotal),
    total: round2(subTotal + taxTotal),
  };
};

interface QuotationFormData {
  customer_id: number;
  customer_snapshot: CustomerSnapshot;
  quote_date: string;
  valid_until: string;
  status: QuotationStatus;
  memo: string;
  notes: string;
  lines: LocalLine[];
}

const lineInputClass =
  'w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500';

const autoResize = (el: HTMLTextAreaElement) => {
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
};

export type QuotationsManageSaveReason = 'created' | 'updated';

export type QuotationsManageProps = {
  embedded?: boolean;
  quotationId?: number;
  onClose?: () => void;
  onQuotationSaved?: (quotation: Quotation, reason: QuotationsManageSaveReason) => void;
};

const QuotationsManage = ({
  embedded = false,
  quotationId: quotationIdProp,
  onClose,
  onQuotationSaved,
}: QuotationsManageProps = {}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryEditId = Number(searchParams.get('id')) || 0;
  const resolvedEditId =
    quotationIdProp != null && quotationIdProp > 0 ? quotationIdProp : queryEditId;
  const isEditMode = resolvedEditId > 0;
  const canManage = canManageQuotations();
  const isReadOnly = !canManage;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[] | undefined>>({});

  const [customerOptions, setCustomerOptions] = useState<SearchableSelectOption[]>([]);
  const [customerSearchInput, setCustomerSearchInput] = useState('');
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [selectedCustomerOption, setSelectedCustomerOption] =
    useState<SearchableSelectOption | null>(null);
  const selectedCustomerOptionRef = useRef<SearchableSelectOption | null>(null);
  const customerLookupRef = useRef<Map<number, Customer>>(new Map());
  const lineRowRefs = useRef(new Map<number, HTMLTableRowElement>());

  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [showCreateItemModal, setShowCreateItemModal] = useState(false);
  const [createItemLineIndex, setCreateItemLineIndex] = useState<number | null>(null);
  const [taxTotalDraft, setTaxTotalDraft] = useState<string | null>(null);
  const [editingQuotation, setEditingQuotation] = useState<Quotation | null>(null);
  const [loadingCustomerAccount, setLoadingCustomerAccount] = useState(false);
  const [customerOpenBalance, setCustomerOpenBalance] = useState(0);
  const [customerUnappliedTotal, setCustomerUnappliedTotal] = useState(0);

  const today = todayLocalIso();
  const [formData, setFormData] = useState<QuotationFormData>({
    customer_id: 0,
    customer_snapshot: emptyCustomerSnapshot(),
    quote_date: today,
    valid_until: '',
    status: 'DRAFT',
    memo: '',
    notes: '',
    lines: [],
  });

  useEffect(() => {
    if (!embedded && !canManage && !isEditMode) {
      navigate('/quotations');
      return;
    }
    if (embedded && !canManage && !isEditMode) {
      onClose?.();
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedEditId, embedded]);

  useEffect(() => {
    selectedCustomerOptionRef.current = selectedCustomerOption;
  }, [selectedCustomerOption]);

  const loadCustomerAccountSummary = useCallback(async (customerId: number) => {
    setLoadingCustomerAccount(true);
    try {
      const [receivableInvoices, unappliedPaymentsList] = await Promise.all([
        getCustomerReceivableInvoices(customerId),
        getCustomerPayments({ customer_id: customerId, unapplied: true }),
      ]);
      const openBalance = receivableInvoices.reduce(
        (sum, invoice) => sum + parseFloat(invoice.balance),
        0
      );
      const unapplied = unappliedPaymentsList
        .filter((payment) => (payment.status ?? 'POSTED') !== 'VOIDED')
        .reduce((sum, payment) => sum + parseFloat(payment.unapplied_amount), 0);
      setCustomerOpenBalance(openBalance);
      setCustomerUnappliedTotal(unapplied);
    } catch {
      setCustomerOpenBalance(0);
      setCustomerUnappliedTotal(0);
    } finally {
      setLoadingCustomerAccount(false);
    }
  }, []);

  useEffect(() => {
    if (formData.customer_id > 0) {
      void loadCustomerAccountSummary(formData.customer_id);
    } else {
      setCustomerOpenBalance(0);
      setCustomerUnappliedTotal(0);
      setLoadingCustomerAccount(false);
    }
  }, [formData.customer_id, loadCustomerAccountSummary]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      setCustomerSearchLoading(true);
      try {
        const result = await getCustomers({
          search: customerSearchInput.trim() || undefined,
          per_page: 20,
          page: 1,
        });
        for (const customer of result.customers) {
          customerLookupRef.current.set(customer.id, customer);
        }
        const options = result.customers.map(customerToOption);
        const selected = selectedCustomerOptionRef.current;
        setCustomerOptions(
          selected && !options.some((opt) => String(opt.value) === String(selected.value))
            ? [selected, ...options]
            : options
        );
      } catch {
        // silently ignore
      } finally {
        setCustomerSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearchInput]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      customerLookupRef.current.clear();
      setCustomerSearchInput('');
      setCustomerOpenBalance(0);
      setCustomerUnappliedTotal(0);
      setLoadingCustomerAccount(false);
      setEditingQuotation(null);

      if (isEditMode) {
        const quotation = await getQuotationById(resolvedEditId);
        setEditingQuotation(quotation);

        const customerLabel = getQuotationCustomerName(quotation);
        const selectedOption: SearchableSelectOption = {
          value: quotation.customer_id,
          label: customerLabel,
          description:
            [quotation.customer_snapshot.email, quotation.customer_snapshot.phone]
              .filter(Boolean)
              .join(' · ') || undefined,
        };
        setSelectedCustomerOption(selectedOption);
        selectedCustomerOptionRef.current = selectedOption;
        setCustomerOptions([selectedOption]);

        const mappedLines = (quotation.lines ?? []).map(mapQuotationLineToLocal);

        setFormData({
          customer_id: quotation.customer_id,
          customer_snapshot: { ...quotation.customer_snapshot },
          quote_date: quotation.quote_date.slice(0, 10),
          valid_until: quotation.valid_until ? quotation.valid_until.slice(0, 10) : '',
          status: quotation.status,
          memo: quotation.memo ?? '',
          notes: quotation.notes ?? '',
          lines: isReadOnly ? mappedLines : withTrailingEmptyLine(mappedLines),
        });
      } else {
        setSelectedCustomerOption(null);
        selectedCustomerOptionRef.current = null;
        setCustomerOptions([]);
        setFormData((prev) => ({
          ...prev,
          customer_snapshot: emptyCustomerSnapshot(),
          lines: createInitialLines(),
        }));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerChange = useCallback(
    async (customerId: number) => {
      const id = Number(customerId);
      if (!id) {
        setSelectedCustomerOption(null);
        selectedCustomerOptionRef.current = null;
        setCustomerOpenBalance(0);
        setCustomerUnappliedTotal(0);
        setLoadingCustomerAccount(false);
        setFormData((prev) => ({
          ...prev,
          customer_id: 0,
          customer_snapshot: emptyCustomerSnapshot(),
        }));
        return;
      }

      let customer = customerLookupRef.current.get(id);
      if (!customer) {
        try {
          customer = await getCustomerById(id);
          customerLookupRef.current.set(id, customer);
        } catch {
          setFormData((prev) => ({ ...prev, customer_id: id }));
          return;
        }
      }

      const option = customerToOption(customer);
      setSelectedCustomerOption(option);
      selectedCustomerOptionRef.current = option;
      setCustomerOptions((prev) =>
        prev.some((opt) => String(opt.value) === String(id)) ? prev : [option, ...prev]
      );
      setFormData((prev) => ({
        ...prev,
        customer_id: id,
        customer_snapshot: customerToSnapshot(customer),
      }));
    },
    []
  );

  const resetBillToFromCustomer = async () => {
    const id = formData.customer_id;
    if (!id) return;

    let customer = customerLookupRef.current.get(id);
    if (!customer) {
      try {
        customer = await getCustomerById(id);
        customerLookupRef.current.set(id, customer);
      } catch {
        toast.error('Could not load customer details.');
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      customer_snapshot: customerToSnapshot(customer),
    }));
  };

  const updateCustomerSnapshot = (patch: Partial<CustomerSnapshot>) => {
    setFormData((prev) => ({
      ...prev,
      customer_snapshot: { ...prev.customer_snapshot, ...patch },
    }));
  };

  const handleCustomerSaved = (customer: Customer) => {
    customerLookupRef.current.set(customer.id, customer);
    setShowAddCustomerModal(false);
    void handleCustomerChange(customer.id);
    toast.success(`Customer "${customer.display_name}" created and selected.`);
  };

  const updateLine = (index: number, patch: Partial<LocalLine>) => {
    setFormData((prev) => ({
      ...prev,
      lines: prev.lines.map((line, i) => (i === index ? { ...line, ...patch } : line)),
    }));
  };

  const selectItemForLine = (index: number, item: Item | null) => {
    setFormData((prev) => {
      const lines = [...prev.lines];
      const current = lines[index] ?? createEmptyLine();

      if (!item) {
        lines[index] = createEmptyLine();
        const trimmed = lines.filter(
          (line, i) => line.item_id > 0 || i === lines.length - 1
        );
        return {
          ...prev,
          lines: trimmed.length > 0 ? withTrailingEmptyLine(trimmed) : [createEmptyLine()],
        };
      }

      lines[index] = {
        ...current,
        item_id: item.id,
        item_name: item.name,
        item_sku: item.sku ?? '',
        item_type: item.item_type,
        qty_on_hand: item.qty_on_hand,
        dealer_selling_price: item.dealer_selling_price,
        cash_price: item.cash_price,
        unit_price: parseFloat(item.unit_price) || 0,
        description: item.description?.trim() ?? '',
      };

      return { ...prev, lines: withTrailingEmptyLine(lines) };
    });

    if (item) {
      const nextIndex = index + 1;
      scrollIntoViewAfterPaint(() => lineRowRefs.current.get(nextIndex));
    }
  };

  const openCreateItemModal = (lineIndex: number) => {
    setCreateItemLineIndex(lineIndex);
    setShowCreateItemModal(true);
  };

  const handleItemCreated = (item: Item, meta?: { imageError?: string }) => {
    if (meta?.imageError) {
      return;
    }

    if (createItemLineIndex !== null) {
      selectItemForLine(createItemLineIndex, item);
    }
    setShowCreateItemModal(false);
    setCreateItemLineIndex(null);
  };

  const applyQuotationTax = (totalTax: number) => {
    setFormData((prev) => {
      const activeIndices = prev.lines
        .map((line, index) => (line.item_id > 0 ? index : -1))
        .filter((index) => index >= 0);
      if (activeIndices.length === 0) return prev;

      const nets = activeIndices.map((index) => computeLineTotals(prev.lines[index]).net);
      const totalNet = nets.reduce((sum, net) => sum + net, 0);
      const lines = [...prev.lines];
      let allocated = 0;

      activeIndices.forEach((lineIndex, activeIndex) => {
        const isLast = activeIndex === activeIndices.length - 1;
        const share =
          totalNet > 0
            ? round2(totalTax * (nets[activeIndex] / totalNet))
            : round2(totalTax / activeIndices.length);
        const amount = isLast ? round2(totalTax - allocated) : share;
        allocated += amount;
        lines[lineIndex] = { ...lines[lineIndex], tax_amount: Math.max(0, amount) };
      });

      return { ...prev, lines };
    });
  };

  const handleRemoveLine = (index: number) => {
    setFormData((prev) => {
      const updated = prev.lines.filter((_, i) => i !== index);
      return {
        ...prev,
        lines: withTrailingEmptyLine(
          updated.filter((l) => l.item_id > 0)
        ),
      };
    });
  };

  const validateForm = (): boolean => {
    if (!formData.customer_id) {
      toast.error('Please select a customer.');
      return false;
    }
    const snapshotError = validateCustomerSnapshot(formData.customer_snapshot);
    if (snapshotError) {
      toast.error(snapshotError);
      return false;
    }
    if (!formData.quote_date) {
      toast.error('Quote date is required.');
      return false;
    }
    const activeLines = formData.lines.filter((l) => l.item_id > 0);
    if (activeLines.length === 0) {
      toast.error('Add at least one line item.');
      return false;
    }
    for (const line of activeLines) {
      if (line.quantity <= 0) {
        toast.error('All quantities must be greater than 0.');
        return false;
      }
      if (line.unit_price < 0) {
        toast.error('Unit price cannot be negative.');
        return false;
      }
      if (line.description.length > 1000) {
        toast.error(
          `Description is too long for ${line.item_name} (max 1000 characters).`
        );
        return false;
      }
    }
    return true;
  };

  const buildCreatePayload = (): CreateQuotationData => {
    const activeLines = formData.lines.filter((l) => l.item_id > 0);
    return {
      customer_id: formData.customer_id,
      customer_snapshot: snapshotToPayload(formData.customer_snapshot),
      quote_date: formData.quote_date,
      valid_until: formData.valid_until || null,
      status: formData.status,
      memo: formData.memo || null,
      notes: formData.notes || null,
      lines: activeLines.map((line) => ({
        item_id: line.item_id,
        description: line.description.trim() || null,
        quantity: line.quantity,
        unit_price: line.unit_price,
        discount_amount: line.discount_amount || 0,
        tax_amount: line.tax_amount || 0,
      })),
    };
  };

  const buildUpdatePayload = (): UpdateQuotationData => {
    const activeLines = formData.lines.filter((l) => l.item_id > 0);
    return {
      customer_id: formData.customer_id,
      customer_snapshot: snapshotToPayload(formData.customer_snapshot),
      quote_date: formData.quote_date,
      valid_until: formData.valid_until || null,
      status: formData.status,
      memo: formData.memo || null,
      notes: formData.notes || null,
      lines: activeLines.map((line) => ({
        item_id: line.item_id,
        description: line.description.trim() || null,
        quantity: line.quantity,
        unit_price: line.unit_price,
        discount_amount: line.discount_amount || 0,
        tax_amount: line.tax_amount || 0,
      })),
    };
  };

  const handleSave = async () => {
    blurActiveInputBeforeSave();
    setError(null);
    setFieldErrors({});
    if (!validateForm()) return;

    try {
      setSubmitting(true);

      if (isEditMode) {
        const quotation = await updateQuotation(resolvedEditId, buildUpdatePayload());
        if (embedded) {
          onQuotationSaved?.(quotation, 'updated');
          toast.success('Quotation updated.');
          return;
        }
        navigate('/quotations', {
          state: { successMessage: 'Quotation updated.' },
        });
      } else {
        const quotation = await createQuotation(buildCreatePayload());
        if (embedded) {
          onQuotationSaved?.(quotation, 'created');
          toast.success('Quotation created.');
          return;
        }
        navigate('/quotations', {
          state: { successMessage: 'Quotation created.' },
        });
      }
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setFieldErrors(err.errors);
        setError(err.message);
      } else {
        toastApiError(err, 'Failed to save quotation');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const fieldError = (field: string) => fieldErrors[field]?.[0];

  const estimatedTotals = useMemo(
    () => computeQuotationTotals(formData.lines),
    [formData.lines]
  );

  const displayTotals = useMemo(() => {
    if (isReadOnly && editingQuotation) {
      return {
        subTotal: parseFloat(editingQuotation.sub_total) || 0,
        discountTotal: parseFloat(editingQuotation.discount_total) || 0,
        taxTotal: parseFloat(editingQuotation.tax_total) || 0,
        total: parseFloat(editingQuotation.total_amount) || 0,
      };
    }
    return estimatedTotals;
  }, [isReadOnly, editingQuotation, estimatedTotals]);

  const hasLineItems = formData.lines.some((l) => l.item_id > 0);

  if (loading) {
    if (embedded) {
      return (
        <div className="flex flex-1 items-center justify-center min-h-[12rem]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800" />
        </div>
      );
    }

    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-800" />
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? 'flex flex-col h-full min-h-0 bg-gray-50' : 'flex h-screen bg-gray-50'}>
      {!embedded && <Sidebar />}

      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        <div className={`flex-1 overflow-auto ${embedded ? 'p-4 pb-28' : 'p-6 pb-28'}`}>
          {/* Page header */}
          <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
            <div>
              {!embedded && (
                <Link
                  to="/quotations"
                  className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-3"
                >
                  <ArrowLeft className="h-4 w-4 mr-1.5" />
                  Back to Quotations
                </Link>
              )}
              <h1 className="text-2xl font-semibold text-gray-900">
                {isEditMode
                  ? isReadOnly
                    ? `View Quotation ${editingQuotation?.quotation_number ?? ''}`
                    : `Edit Quotation ${editingQuotation?.quotation_number ?? ''}`
                  : 'New Quotation'}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {isReadOnly
                  ? 'Read-only view — quotations do not affect ledger or inventory'
                  : 'Informational quote — no ledger, inventory, or payment effects'}
              </p>
              {isEditMode && editingQuotation && (
                <p className="text-sm text-gray-500 mt-2">
                  Quotation #{editingQuotation.quotation_number}
                  {' · '}
                  <span
                    className={`font-medium ${
                      editingQuotation.status === 'ACCEPTED'
                        ? 'text-green-700'
                        : editingQuotation.status === 'REJECTED'
                          ? 'text-red-600'
                          : 'text-gray-700'
                    }`}
                  >
                    {formatLabel(editingQuotation.status)}
                  </span>
                </p>
              )}
            </div>
          </div>

          {/* Error banner */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-400 shrink-0" />
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 md:p-8 space-y-8">
            {/* === Quote details === */}
            <section className="pb-8 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900 mb-5">Quote details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <div className="sm:col-span-2 xl:col-span-4">
                  <div className="grid grid-cols-1 gap-4">
                    <div className="min-w-0">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Customer <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2">
                        <SearchableSelect
                          className="flex-1 min-w-0"
                          value={formData.customer_id}
                          onChange={(id) => void handleCustomerChange(Number(id))}
                          placeholder="Select customer"
                          searchPlaceholder="Search customers..."
                          options={customerOptions}
                          onSearchChange={setCustomerSearchInput}
                          isLoading={customerSearchLoading}
                          disabled={isReadOnly}
                        />
                        {!isReadOnly && (
                          <button
                            type="button"
                            onClick={() => setShowAddCustomerModal(true)}
                            className="flex-shrink-0 inline-flex items-center px-3 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 bg-slate-50 hover:bg-slate-100"
                            title="Create new customer"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      {fieldError('customer_id') && (
                        <p className="mt-1 text-sm text-red-600">{fieldError('customer_id')}</p>
                      )}
                    </div>

                    {formData.customer_id > 0 && (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center justify-between gap-3 mb-1.5">
                            <label className="block text-sm font-medium text-gray-700">
                              Bill to{' '}
                              <span className="font-normal text-gray-500">(printed on quote)</span>
                            </label>
                            {!isReadOnly && (
                              <button
                                type="button"
                                onClick={() => void resetBillToFromCustomer()}
                                className="text-xs font-medium text-slate-600 hover:text-slate-900 shrink-0"
                              >
                                Reset to customer
                              </button>
                            )}
                          </div>
                          <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-3 space-y-2">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                              <div className="min-w-0">
                                <input
                                  type="text"
                                  maxLength={255}
                                  value={formData.customer_snapshot.name}
                                  onChange={(e) => updateCustomerSnapshot({ name: e.target.value })}
                                  placeholder="Name *"
                                  disabled={isReadOnly}
                                  className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-700"
                                />
                                {fieldError('customer_snapshot.name') && (
                                  <p className="mt-0.5 text-xs text-red-600">
                                    {fieldError('customer_snapshot.name')}
                                  </p>
                                )}
                              </div>
                              <div className="min-w-0">
                                <input
                                  type="email"
                                  value={formData.customer_snapshot.email ?? ''}
                                  onChange={(e) =>
                                    updateCustomerSnapshot({ email: e.target.value || null })
                                  }
                                  placeholder="Email"
                                  disabled={isReadOnly}
                                  className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-700"
                                />
                                {fieldError('customer_snapshot.email') && (
                                  <p className="mt-0.5 text-xs text-red-600">
                                    {fieldError('customer_snapshot.email')}
                                  </p>
                                )}
                              </div>
                              <div className="min-w-0">
                                <input
                                  type="text"
                                  maxLength={50}
                                  value={formData.customer_snapshot.phone ?? ''}
                                  onChange={(e) =>
                                    updateCustomerSnapshot({ phone: e.target.value || null })
                                  }
                                  placeholder="Phone"
                                  disabled={isReadOnly}
                                  className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-700"
                                />
                                {fieldError('customer_snapshot.phone') && (
                                  <p className="mt-0.5 text-xs text-red-600">
                                    {fieldError('customer_snapshot.phone')}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div>
                              <textarea
                                rows={2}
                                maxLength={2000}
                                value={formData.customer_snapshot.address ?? ''}
                                onChange={(e) =>
                                  updateCustomerSnapshot({ address: e.target.value || null })
                                }
                                placeholder="Address"
                                disabled={isReadOnly}
                                className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm bg-white resize-y min-h-[2.5rem] disabled:bg-gray-50 disabled:text-gray-700"
                              />
                              {fieldError('customer_snapshot.address') && (
                                <p className="mt-0.5 text-xs text-red-600">
                                  {fieldError('customer_snapshot.address')}
                                </p>
                              )}
                            </div>
                            <p className="text-xs text-gray-500">
                              Quote only — does not update the customer record.
                            </p>
                          </div>
                        </div>

                        <div className="min-w-0">
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Customer account
                          </label>
                          <div className="rounded-lg border border-gray-200 bg-gray-50/50 px-3 py-2.5 space-y-1.5 text-sm">
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-gray-500">Open balance</span>
                              <span className="font-semibold text-red-600 tabular-nums whitespace-nowrap">
                                {loadingCustomerAccount ? '—' : formatCurrency(customerOpenBalance)}
                              </span>
                            </div>
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-gray-500">Unapplied payment</span>
                              <span className="font-semibold text-amber-700 tabular-nums whitespace-nowrap">
                                {loadingCustomerAccount ? '—' : formatCurrency(customerUnappliedTotal)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Quote date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.quote_date}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, quote_date: e.target.value }))
                    }
                    disabled={isReadOnly}
                    className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm disabled:bg-gray-50 disabled:text-gray-700"
                  />
                  {fieldError('quote_date') && (
                    <p className="mt-1 text-sm text-red-600">{fieldError('quote_date')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Valid until
                  </label>
                  <input
                    type="date"
                    value={formData.valid_until}
                    min={formData.quote_date}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, valid_until: e.target.value }))
                    }
                    disabled={isReadOnly}
                    className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm disabled:bg-gray-50 disabled:text-gray-700"
                  />
                  {fieldError('valid_until') && (
                    <p className="mt-1 text-sm text-red-600">{fieldError('valid_until')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        status: e.target.value as QuotationStatus,
                      }))
                    }
                    disabled={isReadOnly}
                    className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-700"
                  >
                    {QUOTATION_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {formatLabel(s)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </section>

            {/* === Line items === */}
            <section className="pb-8 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900 mb-5">Line items</h3>

              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 text-sm table-fixed">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="w-5 px-0.5 py-2.5 text-center text-xs font-medium text-gray-500 uppercase">
                        #
                      </th>
                      <th className="w-[18%] px-2 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Product / service
                      </th>
                      <th className="w-[28%] px-2 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Description
                      </th>
                      <th className="w-[9%] px-2 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">
                        Qty
                      </th>
                      <th className="w-[11%] px-2 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">
                        Rate
                      </th>
                      <th className="w-[9%] px-2 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">
                        Discount
                      </th>
                      <th className="w-[11%] px-2 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">
                        Amount
                      </th>
                      <th className="w-8 px-1 py-2.5" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {formData.lines.map((line, index) => {
                      const isEmptyRow = line.item_id === 0;
                      const { net } = computeLineTotals(line);

                      return (
                        <tr
                          key={`line-${index}`}
                          ref={(el) => {
                            if (el) lineRowRefs.current.set(index, el);
                            else lineRowRefs.current.delete(index);
                          }}
                          className={`align-top ${isEmptyRow ? 'bg-white' : 'bg-slate-50/40'}`}
                        >
                          <td className="w-5 px-0.5 py-2 text-center text-gray-500 tabular-nums">
                            {index + 1}
                          </td>

                          <td className="px-2 py-2">
                            <div className="flex gap-1">
                              <AsyncItemSelect
                                className="flex-1 min-w-0"
                                value={line.item_id}
                                selectedLabel={line.item_name || undefined}
                                onSelect={(item) => selectItemForLine(index, item)}
                                onClear={() => selectItemForLine(index, null)}
                                placeholder="Select item"
                                searchPlaceholder="Search by name or SKU..."
                                disabled={isReadOnly}
                              />
                              {!isReadOnly && (
                                <button
                                  type="button"
                                  onClick={() => openCreateItemModal(index)}
                                  className="flex-shrink-0 inline-flex items-center px-2 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 bg-slate-50 hover:bg-slate-100"
                                  title="Create new item"
                                >
                                  <Plus className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                            {line.item_id > 0 && (
                              <p className="mt-1 text-xs text-gray-400 font-mono truncate">
                                {line.item_sku}
                              </p>
                            )}
                          </td>

                          <td className="px-2 py-2">
                            <textarea
                              ref={(el) => {
                                if (el) autoResize(el);
                              }}
                              rows={1}
                              maxLength={1000}
                              value={line.description}
                              onChange={(e) => {
                                updateLine(index, { description: e.target.value });
                                autoResize(e.target);
                              }}
                              placeholder="Description"
                              disabled={isReadOnly}
                              className={`${lineInputClass} min-h-[2.25rem] resize-none overflow-hidden disabled:bg-gray-50 disabled:text-gray-700`}
                            />
                          </td>

                          <td className="px-2 py-2">
                            <DecimalInput
                              value={line.quantity}
                              onCommit={(quantity) => updateLine(index, { quantity })}
                              min={0.0001}
                              disabled={isEmptyRow || isReadOnly}
                              className={`${lineInputClass} text-right disabled:bg-gray-50 disabled:text-gray-400`}
                            />
                            {line.item_id > 0 && (
                              <div className="mt-1 space-y-0.5 text-xs text-right">
                                {line.item_type === 'INVENTORY' && (
                                  <p className={`truncate ${qtyOnHandClassName(line.qty_on_hand)}`}>
                                    On hand: {formatPreviewQuantity(line.qty_on_hand)}
                                  </p>
                                )}
                                <p className="truncate">
                                  Dealer: {formatCurrency(line.dealer_selling_price)}
                                </p>
                                <p className="truncate">
                                  Cash: {formatCurrency(line.cash_price)}
                                </p>
                              </div>
                            )}
                          </td>

                          <td className="px-2 py-2">
                            <DecimalInput
                              value={line.unit_price}
                              onCommit={(unit_price) => updateLine(index, { unit_price })}
                              min={0}
                              decimals={4}
                              formatDisplay={(n) => String(displayRate(n))}
                              disabled={isEmptyRow || isReadOnly}
                              className={`${lineInputClass} text-right disabled:bg-gray-50 disabled:text-gray-400`}
                            />
                          </td>

                          <td className="px-2 py-2">
                            <DecimalInput
                              value={line.discount_amount}
                              onCommit={(discount_amount) =>
                                updateLine(index, { discount_amount })
                              }
                              min={0}
                              disabled={isEmptyRow || isReadOnly}
                              className={`${lineInputClass} text-right disabled:bg-gray-50 disabled:text-gray-400`}
                            />
                          </td>

                          <td className="px-2 py-2 text-right text-sm font-medium text-gray-900 tabular-nums">
                            {isEmptyRow ? (
                              <span className="text-gray-300">—</span>
                            ) : (
                              formatCurrency(net)
                            )}
                          </td>

                          <td className="px-1 py-2">
                            {!isEmptyRow && !isReadOnly && (
                              <button
                                type="button"
                                onClick={() => handleRemoveLine(index)}
                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                                title="Remove line"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                <div className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Memo</label>
                      <input
                        type="text"
                        value={formData.memo}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, memo: e.target.value }))
                        }
                        placeholder="Printed on quote"
                        disabled={isReadOnly}
                        className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm disabled:bg-gray-50 disabled:text-gray-700"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Internal Notes
                      </label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, notes: e.target.value }))
                        }
                        rows={3}
                        placeholder="Internal notes only"
                        disabled={isReadOnly}
                        className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm disabled:bg-gray-50 disabled:text-gray-700"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-xl border border-slate-200 p-5">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4">
                    Estimated breakdown
                  </p>
                  {hasLineItems ? (
                    <div className="space-y-2.5 text-sm">
                      {displayTotals.discountTotal > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Discount</span>
                          <span className="font-medium text-red-600">
                            −{formatCurrency(displayTotals.discountTotal)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-600">Subtotal</span>
                        <span className="font-medium text-gray-900">
                          {formatCurrency(displayTotals.subTotal)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-gray-600">Tax</span>
                        {isReadOnly ? (
                          <span className="font-medium text-gray-900">
                            {formatCurrency(displayTotals.taxTotal)}
                          </span>
                        ) : (
                          <input
                            type="number"
                            min={0}
                            step="any"
                            placeholder="0.00"
                            value={
                              taxTotalDraft ??
                              (displayTotals.taxTotal > 0 ? String(displayTotals.taxTotal) : '')
                            }
                            onFocus={() => {
                              setTaxTotalDraft(
                                displayTotals.taxTotal > 0 ? String(displayTotals.taxTotal) : ''
                              );
                            }}
                            onChange={(e) => setTaxTotalDraft(e.target.value)}
                            onBlur={() => {
                              const val = parseFloat(taxTotalDraft ?? '');
                              setTaxTotalDraft(null);
                              if (!isNaN(val) && val >= 0) applyQuotationTax(val);
                            }}
                            className="w-28 border border-gray-300 rounded px-2 py-1 text-right text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500"
                          />
                        )}
                      </div>
                      <div className="flex justify-between pt-3 mt-1 border-t border-slate-200">
                        <span className="text-base font-semibold text-gray-900">Total</span>
                        <span className="text-base font-bold text-slate-800">
                          {formatCurrency(displayTotals.total)}
                        </span>
                      </div>
                      {!isReadOnly && (
                        <p className="text-xs text-gray-400 pt-1">
                          Estimated — server recalculates on save
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Add line items to see totals</p>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>

        <div className="flex-shrink-0 border-t border-gray-200 bg-white px-6 py-4 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="text-sm text-gray-600">
              {hasLineItems ? (
                <>
                  <span className="text-gray-500 mr-3">
                    Subtotal {formatCurrency(displayTotals.subTotal)}
                  </span>
                  <span className="text-gray-500 mr-3">
                    Tax {formatCurrency(displayTotals.taxTotal)}
                  </span>
                  <span className="text-lg font-bold text-slate-800">
                    {formatCurrency(displayTotals.total)}
                  </span>
                </>
              ) : (
                <span className="text-gray-400">No line items</span>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => (embedded ? onClose?.() : navigate('/quotations'))}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700"
              >
                {isReadOnly ? 'Back' : 'Cancel'}
              </button>
              {!isReadOnly && (
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={submitting || !hasLineItems}
                  className={`${btnPrimary} px-6 py-2.5 text-sm font-semibold disabled:opacity-50`}
                >
                  {submitting ? 'Saving...' : isEditMode ? 'Save Changes' : 'Save Quotation'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showAddCustomerModal && (
        <CustomerFormModal
          subtitle="Adds the customer to your list and selects them on this quote."
          onClose={() => setShowAddCustomerModal(false)}
          onSaved={handleCustomerSaved}
        />
      )}

      {showCreateItemModal && (
        <ItemFormModal
          subtitle="Adds the item to your catalog and selects it on this line."
          onClose={() => {
            setShowCreateItemModal(false);
            setCreateItemLineIndex(null);
          }}
          onSaved={handleItemCreated}
        />
      )}
    </div>
  );
};

export default QuotationsManage;
